import { Link } from "react-router-dom";
import { Sparkles, ArrowUpRight } from "lucide-react";
import { formatPrice } from "./ProductCardV2";

/**
 * Two related-product surfaces stacked vertically on the product page:
 *   1. <BundleSuggestions>            — admin-curated "bundle_with"
 *   2. <FrequentlyBoughtTogetherList> — admin-curated "frequently_bought_with"
 *
 * Both accept an array of full resolved products. Renders nothing when
 * the curated list is empty (no algorithmic fallback in Phase-2 to keep
 * the catalog editorial-quality).
 */
function ResolvedCard({ p }) {
  const heroUrl = p.hero_image?.url || p.gallery?.[0]?.url || "";
  return (
    <Link
      to={`/shop/${p.slug}`}
      className="group rounded-2xl border border-emerald-100 bg-white p-3 flex gap-3 items-center hover:border-emerald-300 hover:shadow-sm transition"
      data-testid={`shop-bundle-card-${p.slug}`}
    >
      <div className="w-16 h-16 rounded-xl bg-emerald-50 overflow-hidden flex-shrink-0">
        {heroUrl ? (
          <img src={heroUrl} alt={p.name} className="w-full h-full object-cover" loading="lazy" />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-emerald-700/30">
            <Sparkles className="w-5 h-5" strokeWidth={1.4} />
          </div>
        )}
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-sm font-semibold text-emerald-950 truncate">{p.name}</div>
        {p.short_description && (
          <div className="text-[11px] text-emerald-900/65 truncate mt-0.5">{p.short_description}</div>
        )}
        <div className="text-[12px] text-emerald-700 font-display mt-1">{formatPrice(p.price, p.currency)}</div>
      </div>
      <ArrowUpRight className="w-4 h-4 text-emerald-700/60 group-hover:text-emerald-700 transition" />
    </Link>
  );
}

export function BundleSuggestions({ items }) {
  if (!items || items.length === 0) return null;
  return (
    <section className="rounded-3xl border border-amber-200/60 bg-amber-50/40 p-5 sm:p-7" data-testid="shop-bundle-suggestions">
      <p className="text-[11px] uppercase tracking-[0.2em] text-amber-700 font-semibold">
        Often paired with this
      </p>
      <h3 className="font-display text-xl sm:text-2xl text-emerald-950 font-light leading-tight mt-1">
        Curated bundles for deeper results.
      </h3>
      <p className="text-sm text-emerald-900/70 mt-2 leading-relaxed">
        Hand-picked combinations the wellness team often recommends together.
      </p>
      <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-2.5">
        {items.map((p) => <ResolvedCard key={p.id} p={p} />)}
      </div>
    </section>
  );
}

export function FrequentlyBoughtTogetherList({ items }) {
  if (!items || items.length === 0) return null;
  return (
    <section className="rounded-3xl border border-emerald-100 bg-white p-5 sm:p-7" data-testid="shop-fbt-list">
      <p className="text-[11px] uppercase tracking-[0.2em] text-emerald-700 font-semibold">
        Members also like
      </p>
      <h3 className="font-display text-xl sm:text-2xl text-emerald-950 font-light leading-tight mt-1">
        Frequently bought together.
      </h3>
      <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-2.5">
        {items.map((p) => <ResolvedCard key={p.id} p={p} />)}
      </div>
    </section>
  );
}
